import { NODE_WIDTH, nodeSize } from "./nodeDisplay";
import type { FlowDocument, FlowNodePosition } from "./types";

// Cards are NODE_WIDTH wide with a variable height (one row per port,
// nodeDisplay.ts), so columns leave room for edges between them and rows
// stack by each card's own height rather than a fixed pitch.
export const COLUMN_WIDTH = NODE_WIDTH + 100;
const ROW_GAP = 40;

/**
 * Positions every node for a read-only preview.
 *
 * If every node already carries an editor `position` (docs/plans/06: "Nodes
 * have id/type/config and optional editor position"), those are used as-is
 * — this is what a real designer would have saved, and "Layout changes
 * alone cannot change behavior" cuts both ways: a saved layout must render
 * as saved, not be silently recomputed.
 *
 * None of the three shipped examples carry positions yet, so the common
 * case today is the fallback: a deterministic layered auto-layout. Column =
 * the LONGEST path (in edge hops) from start_node_id over the graph's
 * forward edges, so every node sits to the right of everything that leads
 * into it — shared endings such as a final Hang up land in the last column
 * instead of mid-graph with edges doubling back. Back-edges (the bounded
 * Repeat cycles the schema allows) are found with a depth-first walk from
 * start and left out of that calculation, which is what keeps it finite.
 * Row = document order within a column, stacked by each card's height.
 *
 * A node unreachable from start (schema-invalid per semantic rule "no
 * unreachable node", but this function does not itself validate) is placed
 * in one trailing column so it is still visible rather than silently
 * dropped.
 */
export function layoutFlow(doc: FlowDocument): Record<string, FlowNodePosition> {
  if (doc.nodes.length > 0 && doc.nodes.every((node) => node.position)) {
    const positions: Record<string, FlowNodePosition> = {};
    for (const node of doc.nodes) {
      positions[node.id] = node.position as FlowNodePosition;
    }
    return positions;
  }

  const outgoing = new Map<string, string[]>();
  for (const node of doc.nodes) {
    outgoing.set(node.id, []);
  }
  for (const edge of doc.edges) {
    outgoing.get(edge.source)?.push(edge.target);
  }

  // Depth-first from start: an edge into a node still on the current path
  // is a back-edge (a cycle); everything else is a forward edge. `order` is
  // the reverse post-order, i.e. a topological order of the forward graph.
  const forward = new Map<string, string[]>();
  const order: string[] = [];
  const state = new Map<string, "active" | "done">();
  function visit(id: string) {
    state.set(id, "active");
    const kept: string[] = [];
    for (const target of outgoing.get(id) ?? []) {
      const seen = state.get(target);
      if (seen === "active") continue;
      kept.push(target);
      if (!seen) visit(target);
    }
    forward.set(id, kept);
    state.set(id, "done");
    order.unshift(id);
  }

  const column = new Map<string, number>();
  if (outgoing.has(doc.start_node_id)) {
    visit(doc.start_node_id);
    column.set(doc.start_node_id, 0);
    for (const id of order) {
      const current = column.get(id) ?? 0;
      for (const target of forward.get(id) ?? []) {
        column.set(target, Math.max(column.get(target) ?? 0, current + 1));
      }
    }
  }

  const unreachableColumn = Math.max(0, ...column.values()) + 1;
  const nextYInColumn = new Map<number, number>();
  const positions: Record<string, FlowNodePosition> = {};
  for (const node of doc.nodes) {
    const nodeColumn = column.get(node.id) ?? unreachableColumn;
    const y = nextYInColumn.get(nodeColumn) ?? 0;
    nextYInColumn.set(nodeColumn, y + nodeSize(doc, node).height + ROW_GAP);
    positions[node.id] = { x: nodeColumn * COLUMN_WIDTH, y };
  }
  return positions;
}
